import { ErrorBoundary } from '@sentry/react';
import { lazy, Suspense, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Route, Routes, useLocation } from 'react-router';

import { trackPageView } from './lib/analytics';
import { routes } from './routes-config';

// Pre-compute lazy components once at module scope so React keeps the
// Suspense cache across renders. Recreating `lazy()` inside the render body
// causes every navigation to re-fetch the chunk.
const LAZY_ROUTES = routes.map((route) => ({
  path: route.path,
  Component: lazy(route.lazy),
}));

const NotFound = lazy(() => import('./routes/not-found'));

function RouteFallback() {
  const { t } = useTranslation();
  return (
    <div
      role="status"
      aria-live="polite"
      className="flex min-h-[60vh] items-center justify-center text-ink/60"
    >
      <span className="animate-pulse text-sm">{t('shell.loading')}</span>
    </div>
  );
}

function RouteAnalytics() {
  const location = useLocation();
  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location.pathname, location.search]);
  return null;
}

/**
 * Last-resort recovery UI for an uncaught render error anywhere in the route
 * tree — including the CMS-driven `LandingNav`/`LandingFooter`, which render
 * outside the per-section `SectionErrorBoundary`. Copy is hardcoded (no `t()`)
 * because i18n may itself be part of what threw; Korean is the canonical
 * fallback (Web/CLAUDE.md). A full reload is the safe reset for a content site
 * with no client-side recoverable state.
 */
function AppErrorFallback() {
  return (
    <div
      role="alert"
      className="flex min-h-screen flex-col items-center justify-center gap-6 bg-paper px-6 text-center"
    >
      <div className="flex flex-col gap-2">
        <h1 className="text-2xl font-semibold text-ink">문제가 발생했습니다</h1>
        <p className="text-pretty break-keep text-base text-ink/60">
          페이지를 불러오는 중 오류가 발생했어요. 잠시 후 다시 시도해 주세요.
        </p>
      </div>
      <button
        type="button"
        onClick={() => window.location.reload()}
        className="inline-flex h-12 items-center justify-center rounded-full bg-brand-500 px-7 text-sm font-semibold text-paper shadow-sm transition-all hover:-translate-y-0.5 hover:bg-brand-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2"
      >
        새로고침
      </button>
    </div>
  );
}

export function App() {
  return (
    // Top-level boundary: a throw anywhere in the route tree (legal routes, the
    // CMS chrome, lazy-loaded pages) is caught and reported to Sentry instead of
    // unmounting the whole app to a blank page. The per-section
    // `SectionErrorBoundary` still isolates individual landing sections beneath
    // this. `showDialog` is off — we render our own on-brand fallback.
    <ErrorBoundary fallback={<AppErrorFallback />}>
      <Suspense fallback={<RouteFallback />}>
        <RouteAnalytics />
        <Routes>
          {LAZY_ROUTES.map(({ path, Component }) => (
            <Route key={path} path={path} element={<Component />} />
          ))}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
}
